'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { Languages, type LucideIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { PolicyPageContent } from '@/components/policy-page-content'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'

import { useLocale } from '@/hooks/use-locale'

import { getTermsQuery, type TermsResponse } from '@/query/config'

type PublicNavKey = 'cancellationPolicy' | 'privacyPolicy' | 'termsConditions'

type PublicPolicyPageProps = {
  activePage: PublicNavKey
  contentKey: keyof TermsResponse
  emptyIcon: LucideIcon
  pageNamespace:
    | 'public.cancellationPolicy'
    | 'public.privacyPolicy'
    | 'public.termsConditions'
}

const navItems: Array<{ href: string; key: PublicNavKey }> = [
  { href: '/privacy-policy', key: 'privacyPolicy' },
  { href: '/terms-conditions', key: 'termsConditions' },
  { href: '/cancellation-policy', key: 'cancellationPolicy' }
]

const logoClassName = 'h-8 w-auto md:h-10'

export function PublicPolicyPage({
  activePage,
  contentKey,
  emptyIcon,
  pageNamespace
}: PublicPolicyPageProps) {
  const { locale } = useLocale()
  const [currentLang, setCurrentLang] = useState<'ar' | 'en'>(locale)
  const { data: terms, isLoading } = useQuery(getTermsQuery(currentLang))
  const tPage = useTranslations(pageNamespace)
  const tLanguage = useTranslations('public.languageToggle')

  if (isLoading) {
    return <PublicPolicyLoading />
  }

  return (
    <div className="flex min-h-screen flex-col">
      <PublicPolicyHeader activePage={activePage} />

      <main className="flex-1">
        <div className="mx-auto max-w-4xl px-6 py-16 md:px-8 lg:py-20">
          <div className="mb-8 flex items-start justify-between gap-4">
            <div className="space-y-3">
              <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
                {tPage('title')}
              </h1>
              <p className="text-muted-foreground">{tPage('subtitle')}</p>
            </div>

            <Button
              aria-label={
                currentLang === 'en'
                  ? tLanguage('arabic')
                  : tLanguage('english')
              }
              className="gap-2 rtl:flex-row-reverse"
              onClick={() => setCurrentLang(currentLang === 'en' ? 'ar' : 'en')}
              size="sm"
              variant="outline"
            >
              <Languages className="h-4 w-4" />
              {currentLang === 'en'
                ? tLanguage('arabic')
                : tLanguage('english')}
            </Button>
          </div>

          <PolicyPageContent
            content={terms?.[contentKey]}
            emptyIcon={emptyIcon}
            emptyStateLabel={tPage('emptyState')}
            lang={currentLang}
          />
        </div>
      </main>

      <PublicPolicyFooter />
    </div>
  )
}

function PublicPolicyFooter() {
  const tFooter = useTranslations('public.footer')
  const tNav = useTranslations('public.nav')

  return (
    <footer className="bg-muted/50 border-t">
      <div className="container mx-auto px-6 py-8">
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <div className="flex items-center gap-3">
            <PublicPolicyLogo />
            <p className="text-muted-foreground text-xs">
              {tFooter('tagline')}
            </p>
          </div>

          <div className="text-muted-foreground flex flex-wrap items-center justify-center gap-6 text-sm">
            {navItems.map((item, index) => (
              <div className="contents" key={item.key}>
                {index > 0 && (
                  <Separator
                    className="hidden h-4 md:block"
                    orientation="vertical"
                  />
                )}
                <Link
                  className="hover:text-foreground transition-colors"
                  href={item.href}
                >
                  {tNav(item.key)}
                </Link>
              </div>
            ))}
            <Separator className="hidden h-4 md:block" orientation="vertical" />
            <span>
              © {new Date().getFullYear()} {tFooter('brandName')}.{' '}
              {tFooter('allRightsReserved')}
            </span>
          </div>
        </div>
      </div>
    </footer>
  )
}

function PublicPolicyHeader({ activePage }: { activePage: PublicNavKey }) {
  const tNav = useTranslations('public.nav')

  return (
    <header className="bg-card/95 sticky top-0 z-50 border-b backdrop-blur">
      <div className="container mx-auto flex h-20 items-center justify-between gap-6 px-6">
        <PublicPolicyLogo />

        <nav
          aria-label={tNav('ariaLabelPolicyPages')}
          className="flex flex-wrap items-center justify-end gap-4 text-sm md:gap-6"
        >
          {navItems.map((item) => (
            <Link
              className={
                item.key === activePage
                  ? 'text-foreground font-medium transition-colors'
                  : 'text-muted-foreground hover:text-foreground transition-colors'
              }
              href={item.href}
              key={item.key}
            >
              {tNav(item.key)}
            </Link>
          ))}
        </nav>
      </div>
    </header>
  )
}

function PublicPolicyLoading() {
  return (
    <div className="flex min-h-screen flex-col">
      <header className="bg-card/95 sticky top-0 z-50 border-b backdrop-blur">
        <div className="container mx-auto flex h-20 items-center px-6">
          <PublicPolicyLogo />
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-4xl px-6 py-16 md:px-8 lg:py-20">
          <Skeleton className="mb-4 h-10 w-64" />
          <Skeleton className="mb-12 h-5 w-96 max-w-full" />
          <Skeleton className="h-[400px] w-full" />
        </div>
      </main>
    </div>
  )
}

function PublicPolicyLogo() {
  const tFooter = useTranslations('public.footer')

  return (
    <Link className="shrink-0" href="/">
      <Image
        alt={tFooter('logoAlt')}
        className={logoClassName}
        height={40}
        priority
        src="/media/logo.svg"
        width={171}
      />
    </Link>
  )
}
